import { useForm } from '@mantine/form';
import { Box, PinInput, Text, Stack, Alert, Button, Paper, Group } from '@mantine/core';
import { IconMail, IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useVerifyEmailMfaMutation, useResendEmailMfaCodeMutation } from '@codezo/graphql';
import { notifications } from '@mantine/notifications';

interface EmailVerificationStepProps {
	data: Record<string, unknown>;
	onDataChange: (_data: Record<string, unknown>) => void;
	onValidSubmit: (_data: Record<string, unknown>) => void;
	onPrevious: () => void;
	isFirstStep: boolean;
	isLastStep: boolean;
	triggerValidation: number;
}

export function EmailVerificationStep({
	data,
	onDataChange,
	onValidSubmit,
	triggerValidation,
}: EmailVerificationStepProps) {
	const lastTriggerRef = useRef(0);
	const [isVerifying, setIsVerifying] = useState(false);
	const [isResending, setIsResending] = useState(false);
	const [verifyEmailMfa] = useVerifyEmailMfaMutation();
	const [resendEmailMfaCode] = useResendEmailMfaCodeMutation();

	const form = useForm({
		initialValues: {
			verificationCode: '',
		},
		validate: {
			verificationCode: (value) => {
				if (!value.trim()) return 'Verification code is required';
				if (!/^\d{6}$/.test(value.trim())) return 'Code must be 6 digits';
				return null;
			},
		},
	});

	const handleSubmit = useCallback(
		async (values: typeof form.values) => {
			setIsVerifying(true);
			try {
				const { data: verifyData } = await verifyEmailMfa({
					variables: {
						input: {
							userId: data.userId as string,
							code: values.verificationCode,
						},
					},
				});

				if (verifyData?.verifyEmailMfa) {
					const { token } = verifyData.verifyEmailMfa;

					// Pass token and success flag to parent
					onValidSubmit({
						...values,
						mfaSetupComplete: true,
						authToken: token,
					});
				}
			} catch {
				// Error handled by mutation onError
				notifications.show({
					title: 'Verification Failed',
					message: 'The code you entered is invalid or has expired. Please try again.',
					color: 'red',
				});
				form.setFieldError('verificationCode', 'Invalid verification code');
			} finally {
				setIsVerifying(false);
			}
		},
		[verifyEmailMfa, data.userId, onValidSubmit, form]
	);

	const handleResendCode = async () => {
		setIsResending(true);
		try {
			const { data: resendData } = await resendEmailMfaCode({
				variables: {
					userId: data.userId as string,
				},
			});

			if (resendData?.resendEmailMfaCode?.success) {
				notifications.show({
					title: 'Code Sent',
					message: `A new verification code has been sent to ${data.email}`,
					color: 'green',
				});
			} else {
				notifications.show({
					title: 'Failed to Send',
					message:
						resendData?.resendEmailMfaCode?.message ||
						'Could not send a new code. Please try again.',
					color: 'red',
				});
			}
		} catch {
			// Error handled by mutation onError
			notifications.show({
				title: 'Failed to Send',
				message: 'Could not send a new code. Please try again.',
				color: 'red',
			});
		} finally {
			setTimeout(() => setIsResending(false), 3000); // Prevent spam
		}
	};

	// Update parent component whenever form values change
	useEffect(() => {
		onDataChange(form.values);
	}, [form.values, onDataChange]);

	// Handle validation trigger from parent
	useEffect(() => {
		if (triggerValidation > 0 && triggerValidation !== lastTriggerRef.current) {
			lastTriggerRef.current = triggerValidation;

			const validationResult = form.validate();

			if (!validationResult.hasErrors) {
				handleSubmit(form.values);
			}
		}
	}, [triggerValidation, form, handleSubmit]);

	// Send initial email on component mount
	useEffect(() => {
		if (data.userId && data.email && !data.emailSent) {
			// Trigger email send
			// In real implementation, this would call a mutation to send the email
			notifications.show({
				title: 'Verification Code Sent',
				message: `We've sent a verification code to ${data.email}`,
				color: 'blue',
				icon: <IconMail size={16} />,
			});

			// Mark email as sent
			onDataChange({ ...data, emailSent: true });
		}
	}, [data.userId, data.email, data, onDataChange]);

	return (
		<Box>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack gap="lg">
					<Alert icon={<IconMail />} color="blue">
						<Text size="sm">
							We&apos;ve sent a verification code to{' '}
							<Text span fw={600}>
								{data.email as string}
							</Text>
							. Please check your email and enter the code below.
						</Text>
					</Alert>

					<Paper withBorder p="md" radius="md">
						<Stack gap="md" align="center">
							<Text size="sm" fw={500}>
								Email Verification
							</Text>

							<Text size="sm" c="dimmed" ta="center">
								Check your email for the verification code
							</Text>

							<PinInput
								length={6}
								type="number"
								size="xl"
								value={form.values.verificationCode}
								onChange={(value) => form.setFieldValue('verificationCode', value)}
								disabled={isVerifying}
								onComplete={() => handleSubmit(form.values)}
								error={!!form.errors.verificationCode}
								autoFocus
							/>

							{isVerifying && (
								<Text size="sm" c="dimmed">
									Verifying...
								</Text>
							)}

							<Group justify="space-between">
								<Text size="xs" c="dimmed">
									Didn&apos;t receive the code?
								</Text>
								<Button
									variant="subtle"
									size="xs"
									onClick={handleResendCode}
									loading={isResending}
									leftSection={<IconRefresh size={14} />}
									disabled={isResending}
								>
									Resend Code
								</Button>
							</Group>

							<Alert color="gray" variant="light">
								<Text size="xs">
									The code will expire in 10 minutes. If you don&apos;t receive
									the email, please check your spam folder.
								</Text>
							</Alert>
						</Stack>
					</Paper>
				</Stack>
			</form>
		</Box>
	);
}
